import { useRef, useState } from "react";

import { useFullTextAssetSearchInfinite } from "~/v2/api/hooks";
import { deriveWatchlistAddSearch } from "./WatchlistAddSearch.derive";
import { WatchlistAddSearchPresentation } from "./WatchlistAddSearch.pres";

export interface WatchlistAddSearchProps {
  membership: ReadonlySet<string>;
  onAdd: (symbol: string) => void | Promise<void>;
  error?: string;
  isPending: boolean;
}

const TRADABLE_CLASSES = new Set(["us_equity", "crypto"]);

/**
 * Wires the header symbol search to full-text asset search. POSTing the
 * symbol to gobroker is the caller's job — `onAdd` only receives the symbol.
 */
export const WatchlistAddSearch = ({
  membership,
  onAdd,
  error,
  isPending,
}: WatchlistAddSearchProps) => {
  const [query, setQuery] = useState("");

  const searchQuery = useFullTextAssetSearchInfinite({
    query,
    assetClass: undefined,
    enabled: query.trim().length > 0,
  });

  const matches = (searchQuery.data?.pages[0]?.assets ?? []).filter(
    (asset) => asset.tradable && TRADABLE_CLASSES.has(asset.class),
  );

  const { results, firstAvailable } = deriveWatchlistAddSearch({
    query,
    matches,
    membership,
  });

  const isSearching = searchQuery.isFetching;
  const addingRef = useRef(false);
  const anchorRef = useRef<HTMLDivElement | null>(null);

  const handleAdd = async (symbol: string) => {
    if (isPending || addingRef.current) {
      return;
    }
    addingRef.current = true;
    // Clearing only on success keeps the popover open (Popover.Root's `open`
    // is derived from `query`) so a caller-set `error` on rejection stays visible.
    // The caller owns the `error` prop, so the rejection itself is swallowed here.
    try {
      await onAdd(symbol);
      setQuery("");
    } catch {
      // no-op — query intentionally left in place
    } finally {
      addingRef.current = false;
    }
  };

  return (
    <WatchlistAddSearchPresentation
      anchorRef={anchorRef}
      query={query}
      onQueryChange={setQuery}
      results={results}
      firstAvailable={firstAvailable}
      error={error}
      isPending={isPending}
      isSearching={isSearching}
      onAdd={handleAdd}
      onClear={() => setQuery("")}
    />
  );
};
